import { computed, defineComponent, ref } from 'vue'
import _ from 'lodash'
import notificationRest from '@/services/clients/notification'
import store from '@/store'
import { extractErrorMessage } from '@/utils/misc'

// Copied from Firebase Remote Config.
// If we start using Firebase Remote Config in Console, then we pull it from there and remove these hardcoded lists.
const ADMIN_NOTIFICATION_PREFERENCES = [
  {
    "id": "discipline_event",
    "en": "Attendance events",
    "es": "Eventos de asistencia",
    "perm": "discipline"
  },
  {
    "id": "discipline_infraction",
    "en": "Attendance infractions",
    "es": "Infracciones de asistencia",
    "perm": "discipline"
  },
  {
    "id": "user_credential",
    "en": "Credential alerts",
    "es": "Alertas de credenciales"
  },
  {
    "id": "custom_field_trigger",
    "en": "Custom field triggers",
    "es": "Disparadores de campo personalizado"
  },
  {
    "id": "face",
    "en": "Face alerts",
    "es": "Alertas faciales",
  },  
  {
    "id": "geo",
    "en": "Geo alerts",
    "es": "Alertas de ubicación"
  },
  {
    "id": "missed_punch_request",
    "en": "Missed punch request alerts",
    "es": "Alertas de solicitud de marcaje perdido"
  },
  {
    "id": "shift_extend_request",
    "en": "Shift extend request alerts",
    "es": "Alertas de solicitud de extensión de turno"
  },  
  {
    "id": "shift_reminder",
    "en": "Shift reminders",
    "es": "Recordatorios de turno"
  },
  {
    "id": "time_off_request",
    "en": "Time off request alerts",
    "es": "Alertas de solicitud de tiempo libre"
  },
  {
    "id": "time_tampered",
    "en": "Time tampered alerts",
    "es": "Alertas de manipulación de tiempo",
    "perm": "clock_alerts"
  }
]
const WORKER_NOTIFICATION_PREFERENCES = [
  {
    "id": "discipline",
    "en": "Attendance alerts",
    "es": "Alertas de asistencia"
  },
  {
    "id": "geo",
    "en": "Geo alerts",
    "es": "Alertas de ubicación"
  },
  {
    "id": "missed_punch_request",
    "en": "Missed punch request alerts",
    "es": "Alertas de solicitud de marcaje perdido"
  },
  {
    "id": "shift_extend_request",
    "en": "Shift extend request alerts",
    "es": "Alertas de solicitud de extensión de turno"
  },  
  {
    "id": "shift_reminder",
    "en": "Shift reminders",
    "es": "Recordatorios de turno"
  },
  {
    "id": "time_off_request",
    "en": "Time off request alerts",
    "es": "Alertas de solicitud de tiempo libre"
  },
  {
    "id": "time_tampered",
    "en": "Time tampered alerts",
    "es": "Alertas de manipulación de tiempo"
  }
]

export function openNotificationPreferences (dialog) {
  const closable = ref(true)

  const DialogContent = defineComponent({
    setup (props, context) {
      const language = ref('en')
      const loading = ref(true)
      const loadErrorMessage = ref(null)
      const saving = ref(false)
      const saveErrorMessage = ref(null)

      const notificationSettings = ref(null)
      const formPreferenceSections = ref([])
      const originalPreferenceSections = ref([])

      const isWorker = computed(() => notificationSettings.value?.roles?.includes('worker'))
      const isDirty = computed(() => !_.isEqual(originalPreferenceSections.value, formPreferenceSections.value))

      const orgId = store.state.organizationId
      const orgUserId = store.state.orgUserId

      async function save () {
        if (saving.value) return

        saving.value = true
        saveErrorMessage.value = null
        closable.value = false

        // Construct data to save.
        const userNotification = _.cloneDeep(notificationSettings.value.userNotification || {
          id: null,
          orgUserId,
          userId: null,
          adminPreferences: [],
          workerPreferences: []
        })
        userNotification.orgUserId = orgUserId
        userNotification.adminPreferences = formPreferenceSections.value
          .find(section => section.role === 'admin')
          ?.options.map(option => ({
            type: option.id,
            preference: option.value
          })) || []
        if (isWorker.value) {
          userNotification.workerPreferences = formPreferenceSections.value
            .find(section => section.role === 'worker')
            ?.options.map(option => ({
              type: option.id,
              preference: option.value
            })) || []
        }

        // Submit to server.
        try {
          const _response = await notificationRest.put(`/orgs/${orgId}/users/${orgUserId}/preferences`, userNotification)
          // notificationSettings.value.userNotification = response.data
          // TODO: Should we update rest of local state?
          context.emit('close')
        } catch (error) {
          saveErrorMessage.value = extractErrorMessage(error)
        } finally {
          saving.value = false
          closable.value = true
        }

        // TODO: Handle saving preferences, and close.
      }

      function cancel () {
        context.emit('close')
      }

      notificationRest.get(`/orgs/${orgId}/users/${orgUserId}/settings`)
        .then(response => {
          console.log('Loaded notification preferences:', response.data)
          notificationSettings.value = response.data

          // Merge into local state.
          originalPreferenceSections.value = []

          // There are various ways to match the type to the permission, so we check all possibilities.
          const perms = notificationSettings.value.adminNotificationPerms
          const preferenceTypes = ADMIN_NOTIFICATION_PREFERENCES.filter(type =>
            perms.some(perm => perm === type.id || perm === type.perm || perm.startsWith(type.id) || type.id.startsWith(perm) || perm.startsWith(type.perm) || type.perm?.startsWith?.(perm)))
          const adminSection = {
            title: isWorker.value ? 'Admin' : null,
            role: 'admin',
            options: _.sortBy(preferenceTypes.map(type => ({
              id: type.id,
              label: type[language.value] || type['en'] || _.startCase(type.id),
              // Default to ALERT if not set.
              value: notificationSettings.value.notificationUser?.adminPreferences?.find?.(pref => pref.type === type.id)?.preference || 'ALERT'
            })), ['label'])
          }
          originalPreferenceSections.value.push(adminSection)

          if (isWorker.value) {
            const workerSection = {
              title: 'Worker',
              role: 'worker',
              options: WORKER_NOTIFICATION_PREFERENCES.map(type => ({
                id: type.id,
                label: type[language.value] || type['en'] || _.startCase(type.id),
                // Default to ALERT if not set.
                value: notificationSettings.value.notificationUser?.workerPreferences?.find?.(pref => pref.type === type.id)?.preference || 'ALERT'
              }))
            }
            originalPreferenceSections.value.push(workerSection)
          }

          formPreferenceSections.value = _.cloneDeep(originalPreferenceSections.value)
        })
        .catch(error => {
          console.warn('Error loading notification preferences:', error)
          loadErrorMessage.value = extractErrorMessage(error)
        })
        .finally(() => {
          loading.value = false
        })

      return () => (
        <div>
          { loading.value &&
          <div class="tw:text-blue-400">
            <font-awesome-icon icon="circle-notch" spin class="tw:mr-2" />
            Loading...
          </div>
          }

          { (loadErrorMessage.value) &&
          <div class="tw:text-red-400">
            <font-awesome-icon icon="triangle-exclamation" class="tw:mr-2" />
            { loadErrorMessage.value }
          </div>
          }

          { !loading.value && !loadErrorMessage.value &&
          <div>
            { formPreferenceSections.value.map(preferenceSection => (
              <div class="tw:my-3">
                { preferenceSection.title &&
                <div class="tw:font-bold tw:text-center">{preferenceSection.title}</div>
                }
                { preferenceSection.options.map(option => (
                  <div class="tw:flex tw:justify-between tw:my-2">
                    <div class="tw:font-medium">{ option.label }</div>
                    <div class="tw:flex tw:gap-2">
                      <div class="tw:flex tw:items-center">
                        <RadioButton vModel={option.value} inputId="none" value="NONE" />
                        <label for="none" class="tw:ml-2">None</label>
                      </div>
                      <div class="tw:flex tw:items-center">
                        <RadioButton vModel={option.value} inputId="message" value="MESSAGE" />
                        <label for="message" class="tw:ml-2">Inbox</label>
                      </div>
                      <div class="tw:flex tw:items-center">
                        <RadioButton vModel={option.value} inputId="alert" value="ALERT" />
                        <label for="alert" class="tw:ml-2">Alert + Inbox</label>
                      </div>
                    </div>
                  </div>
                ))}
                { preferenceSection.options.length === 0 && (
                  <div class="tw:text-gray-500 tw:italic">No preferences available</div>
                )}
              </div>
            ))}

            { (saveErrorMessage.value) &&
            <div class="tw:text-red-400">
              <font-awesome-icon icon="triangle-exclamation" class="tw:mr-2" />
              { saveErrorMessage.value }
            </div>
            }

            <div class="tw:flex tw:justify-end tw:gap-2 tw:mt-4">
              <Button
                type="button"
                size="small"
                label="Cancel"
                icon="pi pi-times"
                severity="secondary"
                disabled={saving.value}
                onClick={() => cancel()
              }></Button>
              <Button
                type="button"
                size="small"
                label="Save"
                icon={saving.value ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
                disabled={saving.value || !isDirty.value}
                onClick={() => save()}
              ></Button>
            </div>
          </div>
          }

        </div>
      )
    }
  })

  const dialogRef = dialog.open(DialogContent, {
    props: {
      header: 'Notification Preferences',
      style: {
        width: '525px'
      },
      modal: true,
      dismissableMask: false,
      closable,
      baseZIndex: 1103 // over page header
    },
    emits: {
      onClose () {
        dialogRef.close()
      }
    }
  })
}
